/// <reference path="./moment-locales.d.ts" />
import { format, formatDistanceToNow, type Locale } from 'date-fns';
import { enUS, fr, pt } from 'date-fns/locale';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/pt';
import moment from 'moment';
import 'moment/locale/fr';
import 'moment/locale/pt';
import { registerLocale, setDefaultLocale } from 'react-datepicker';
import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  type SupportedLanguage,
} from './config';

/**
 * Date locales per UI language. moment, dayjs and react-datepicker are
 * switched globally with the UI language; date-fns and Intl take the
 * locale per call via `getDateFnsLocale()` / `getIntlLocale()`.
 */
const DATE_LOCALES: Record<
  SupportedLanguage,
  { base: string; intl: string; dateFns: Locale }
> = {
  en: { base: 'en', intl: 'en-US', dateFns: enUS },
  fr: { base: 'fr', intl: 'fr-FR', dateFns: fr },
  'pt-PT': { base: 'pt', intl: 'pt-PT', dateFns: pt },
};

let current: SupportedLanguage = DEFAULT_LANGUAGE;

for (const [language, { dateFns }] of Object.entries(DATE_LOCALES)) {
  registerLocale(language, dateFns);
}

/** Applies `language` to moment, dayjs and react-datepicker. */
export const applyDateLocale = (language: string): void => {
  current = isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
  const { base } = DATE_LOCALES[current];
  moment.locale(base);
  dayjs.locale(base);
  setDefaultLocale(current);
};

/** date-fns locale for the UI language: `format(d, 'PP', { locale: getDateFnsLocale() })`. */
export const getDateFnsLocale = (): Locale => DATE_LOCALES[current].dateFns;

/** BCP 47 tag for `Intl.DateTimeFormat` / `toLocaleDateString`. */
export const getIntlLocale = (): string => DATE_LOCALES[current].intl;

// Importing moment locale files switches moment's global locale; start in
// English so apps that never call `initI18n` (e.g. web-app-admin) stay English.
applyDateLocale(DEFAULT_LANGUAGE);

type FormatOptions = Parameters<typeof format>[2];
type DistanceOptions = Parameters<typeof formatDistanceToNow>[1];

/** date-fns `format` in the UI language. */
export const formatDate = (
  date: Date | number | string,
  pattern: string,
  options?: FormatOptions,
): string => format(date, pattern, { locale: getDateFnsLocale(), ...options });

/** date-fns `formatDistanceToNow` in the UI language ("3 minutes ago"). */
export const formatRelativeToNow = (
  date: Date | number | string,
  options?: DistanceOptions,
): string =>
  formatDistanceToNow(date, { locale: getDateFnsLocale(), ...options });
